import React from 'react';

import { useIntl } from 'react-intl-next';

import Button, { type ButtonProps, IconButton, type IconButtonProps } from '@atlaskit/button/new';
import LegacyButton, {
	type ButtonProps as LegacyButtonProps,
} from '@atlaskit/button/standard-button';
import CrossIcon from '@atlaskit/icon/core/migration/close--cross';
import TrashIcon from '@atlaskit/icon/core/migration/delete--trash';
import EditFilledIcon from '@atlaskit/icon/core/migration/edit--edit-filled';
import ShortcutIcon from '@atlaskit/icon/core/migration/link-external--shortcut';
import Tooltip from '@atlaskit/tooltip';

import { messages as commonMessages } from '../../../messages';

import { messages } from './messages';

const CloseButton = React.forwardRef<HTMLButtonElement, Partial<IconButtonProps>>((props, ref) => {
	const intlApi = useIntl();

	return (
		<Tooltip content={intlApi.formatMessage(commonMessages.close)} position="bottom">
			<IconButton
				{...props}
				label={intlApi.formatMessage(commonMessages.close)}
				ref={ref}
				appearance="subtle"
				icon={CrossIcon}
			/>
		</Tooltip>
	);
});

const CancelButton = React.forwardRef<HTMLButtonElement, Partial<ButtonProps>>((props, ref) => {
	const intlApi = useIntl();

	return (
		<Button {...props} ref={ref} appearance="subtle">
			{intlApi.formatMessage(commonMessages.cancel)}
		</Button>
	);
});

const OverrideButton = React.forwardRef<HTMLButtonElement, Partial<ButtonProps>>((props, ref) => {
	const intlApi = useIntl();

	return (
		<Button {...props} ref={ref}>
			{intlApi.formatMessage(messages.override)}
		</Button>
	);
});

const AddOverrideButton = React.forwardRef<HTMLButtonElement, Partial<ButtonProps>>(
	(props, ref) => {
		const intlApi = useIntl();

		return (
			<Button {...props} ref={ref}>
				{intlApi.formatMessage(messages.addOverride)}
			</Button>
		);
	},
);

const EditOverrideButton = React.forwardRef<HTMLButtonElement, Partial<IconButtonProps>>(
	(props, ref) => {
		const intlApi = useIntl();

		return (
			<Tooltip content={intlApi.formatMessage(messages.editOverride)} position="bottom">
				<IconButton
					{...props}
					ref={ref}
					appearance="subtle"
					label={intlApi.formatMessage(messages.editOverride)}
					icon={EditFilledIcon}
				/>
			</Tooltip>
		);
	},
);

const RevertOverrideButton = React.forwardRef<HTMLButtonElement, Partial<IconButtonProps>>(
	(props, ref) => {
		const intlApi = useIntl();

		return (
			<Tooltip content={intlApi.formatMessage(messages.removeOverride)} position="bottom">
				<IconButton
					{...props}
					ref={ref}
					appearance="subtle"
					label={intlApi.formatMessage(messages.removeOverride)}
					icon={TrashIcon}
				/>
			</Tooltip>
		);
	},
);

const EditDetailsButton = React.forwardRef<HTMLButtonElement, Partial<LegacyButtonProps>>(
	(props, ref) => {
		const intlApi = useIntl();

		return (
			<LegacyButton
				{...props}
				ref={ref}
				appearance="link"
				spacing="none"
				iconAfter={<ShortcutIcon color="currentColor" label="" />}
			>
				{intlApi.formatMessage(messages.editDetails)}
			</LegacyButton>
		);
	},
);

export {
	AddOverrideButton,
	OverrideButton,
	CancelButton,
	CloseButton,
	EditDetailsButton,
	EditOverrideButton,
	RevertOverrideButton,
};
